import { ROUTE_STACK_GROUPS } from './groups'
import store from '@/store'

const STORAGE_PREFIX = 'routeStack:'

/** 内存栈缓存 */
const stackCache = {}
/** 浏览器 history 是否与栈同步（刷新后多为 false） */
const historySyncedCache = {}

/** 获取 sessionStorage key */
const getStorageKey = groupKey => `${STORAGE_PREFIX}${groupKey}`

/** 从路由对象生成栈记录 */
export const buildRecord = route => ({
  name: route.name,
  path: route.path,
  query: { ...(route.query || {}) },
  fullPath: route.fullPath
})

/** 从 sessionStorage 读取栈 */
const loadStackFromStorage = groupKey => {
  try {
    const raw = window.sessionStorage.getItem(getStorageKey(groupKey))
    return raw ? JSON.parse(raw) : null
  } catch (error) {
    console.log(error)
    return null
  }
}

/** 持久化栈到 sessionStorage */
const persistStack = (groupKey, stack) => {
  try {
    window.sessionStorage.setItem(
      getStorageKey(groupKey),
      JSON.stringify(stack)
    )
  } catch (error) {
    console.log(error)
  }
}

/** 获取分组栈 */
export const getStack = groupKey => stackCache[groupKey] || []

/** 设置分组栈 */
export const setStack = (groupKey, stack) => {
  stackCache[groupKey] = stack
  persistStack(groupKey, stack)
}

/** 清空分组栈 */
export const clearStack = groupKey => {
  delete stackCache[groupKey]
  delete historySyncedCache[groupKey]
  try {
    window.sessionStorage.removeItem(getStorageKey(groupKey))
  } catch (error) {
    console.log(error)
  }
}

/** history 是否与栈同步 */
export const canUseHistoryGo = groupKey => historySyncedCache[groupKey] === true

/** 标记 history 已与栈同步 */
export const markHistorySynced = groupKey => {
  historySyncedCache[groupKey] = true
}

/** 标记 history 未与栈同步（如刷新后） */
export const markHistoryUnsynced = groupKey => {
  historySyncedCache[groupKey] = false
}

/** 合并公共 query（id/tcode） */
export const pickCommonQuery = (query = {}, extra = {}) => {
  const result = { ...query }
  const id = extra.id ?? query.id
  const tcode = extra.tcode ?? query.tcode
  if (id) result.id = id
  if (tcode) result.tcode = tcode
  return result
}

/** 初始化或恢复当前分组栈 */
export const ensureStackForRoute = (groupKey, to, from) => {
  let stack = stackCache[groupKey]
  if (!stack) {
    stack = loadStackFromStorage(groupKey) || []
  }

  const enteringFromOutside = from.meta?.routeStackGroup !== groupKey

  if (enteringFromOutside) {
    // 如果进入的是外部路由，则清空栈并添加目标路由
    stack = [buildRecord(to)]
    markHistorySynced(groupKey)
  } else if (!from.name) {
    // 通常是页面刷新、直接访问 URL 等场景（没有上一个路由）。
    const idx = stack.findIndex(item => item.fullPath === to.fullPath)
    stack = [...stack]
    if (idx >= 0) {
      stack = stack.slice(0, idx)
    }
    stack.push(buildRecord(to))
    markHistoryUnsynced(groupKey)
  } else if (!stack.length) {
    // 兜底情况，栈里没有任何记录时，直接用当前路由初始化，并标记为已同步。
    stack = [buildRecord(to)]
    markHistorySynced(groupKey)
  }

  setStack(groupKey, stack)
}

/** 浏览器后退/前进时同步栈 */
const syncStackByBrowserNav = (groupKey, to) => {
  const stack = getStack(groupKey)
  const toIndex = stack.findIndex(item => item.fullPath === to.fullPath)

  let nextStack = [...stack]
  if (toIndex >= 0) {
    // 如果目标路由在栈中，则截取到目标路由前一个
    nextStack = nextStack.slice(0, toIndex)
  }
  nextStack.push(buildRecord(to)) //如果目标路由不在栈中，则添加目标路由到栈顶
  setStack(groupKey, nextStack) // 更新栈
  markHistorySynced(groupKey) // 标记历史已同步
}

/** 注册路由栈同步 */
export const installRouteStack = router => {
  router.beforeEach((to, from, next) => {
    const groupKey = to.meta?.routeStackGroup
    const fromGroupKey = from.meta?.routeStackGroup

    const group = groupKey ? ROUTE_STACK_GROUPS[groupKey] : null
    const isLoginPage = group?.login && to.name === group.login
    // 模块鉴权：未登录拦截到模块登录页
    if (group?.login && group?.authModule && !isLoginPage) {
      const authState = store.state[group.authModule] || {}
      const hasToken = !!authState.token
      const isLoggedIn = authState.isLogin === true
      if (!hasToken || !isLoggedIn) {
        return next({
          name: group.login,
          query: { id: to.query.id },
          replace: true
        })
      }
    }

    if (!groupKey) {
      if (fromGroupKey) {
        clearStack(fromGroupKey)
      }
      next()
      return
    }

    if (fromGroupKey === groupKey) {
      syncStackByBrowserNav(groupKey, to)
    } else {
      ensureStackForRoute(groupKey, to, from)
    }

    next()
  })

  router.afterEach((to, from) => {
    const groupKey = to.meta?.routeStackGroup
    if (!groupKey || stackCache[groupKey]) return
    ensureStackForRoute(groupKey, to, from || {})
  })
}
